import { useCallback, useSyncExternalStore } from 'react';

export type MeasureVote = 'yes' | 'no';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is still cleared by `clearVoteMateStorageOnLogout` on
// explicit sign-out.
const STORAGE_KEY = 'votemate_measure_selections';

const isMeasureVote = (value: unknown): value is MeasureVote => value === 'yes' || value === 'no';

const readFromStorage = (): Record<string, MeasureVote> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, vote]) => isMeasureVote(vote))
    ) as Record<string, MeasureVote>;
  } catch {
    return {};
  }
};

const persist = (votes: Record<string, MeasureVote>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(votes));
  } catch {
    // localStorage may be full / disabled (private mode, quota) — fail silently.
  }
};

// Module-level state shared by every consumer of the hook so all measure
// rows stay in sync.
let votes: Record<string, MeasureVote> = readFromStorage();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void): (() => void) => {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
};

// Stable reference per state — `toggleVote` swaps `votes` to a new object
// each mutation, so React's reference-equality check in
// `useSyncExternalStore` detects changes correctly.
const getSnapshot = (): Record<string, MeasureVote> => votes;

// ── Server-sync escape hatches ────────────────────────────────────────────────
// Used by BallotRecordsProvider to mirror votes to the signed-in user's
// current ballot record (PUT /user/ballots/{id}/selections). Guests keep the
// pure-localStorage behavior.

export const getMeasureSelections = (): Record<string, MeasureVote> => ({ ...votes });

export const replaceMeasureSelections = (next: Record<string, string>): void => {
  votes = Object.fromEntries(
    Object.entries(next).filter(([, vote]) => isMeasureVote(vote))
  ) as Record<string, MeasureVote>;
  persist(votes);
  subscribers.forEach((cb) => cb());
};

export const subscribeMeasureSelections = (callback: () => void): (() => void) =>
  subscribe(callback);

/**
 * Returns the current measure-vote map plus a `getVote` lookup and
 * `toggleVote` mutator. Backed by `localStorage` under
 * `votemate_measure_selections`. The key is registered as a persistent key in
 * `storageUtils`, so it survives refresh but is wiped by
 * `clearVoteMateStorageOnLogout` on explicit sign-out.
 *
 * Built on `useSyncExternalStore` for tearing-safe concurrent reads.
 *
 * Signed-in users' votes sync cross-device: BallotRecordsProvider mirrors
 * this store to the current ballot record server-side. Guests stay
 * per-device until they sign in (votes carry over on first sync).
 */
const useMeasureSelections = () => {
  const map = useSyncExternalStore(subscribe, getSnapshot);

  const getVote = useCallback((measureId: string) => map[measureId] ?? null, [map]);

  /** Picking the already-selected choice again clears it back to undecided. */
  const toggleVote = useCallback((measureId: string, choice: MeasureVote): MeasureVote | null => {
    const next = { ...votes };
    const nextVote = next[measureId] === choice ? null : choice;
    if (nextVote) next[measureId] = nextVote;
    else delete next[measureId];
    votes = next;
    persist(votes);
    subscribers.forEach((cb) => cb());
    return nextVote;
  }, []);

  return { getVote, toggleVote };
};

export default useMeasureSelections;
